import { useCallback, useEffect, useRef, useState } from "react";
import { getVideoInferPageApi } from "../../service/videoInfer";

const POLL_MS = 5000;
const LOAD_MORE_MARGIN = 280;

function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function useVideoInferPage(query: {
  name?: string;
  status?: VideoInfer.Status | string | number;
  pageSize?: number;
  enabled?: boolean;
}) {
  const pageSize = query.pageSize ?? 12;
  const enabled = query.enabled !== false;
  const name = query.name?.trim() || undefined;
  const status =
    query.status === undefined || query.status === null
      ? undefined
      : String(query.status).trim() || undefined;

  const [list, setList] = useState<VideoInfer.Item[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasPending, setHasPending] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const pageNoRef = useRef(1);
  const listRef = useRef<VideoInfer.Item[]>([]);
  const totalRef = useRef(0);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(false);
  const reqIdRef = useRef(0);
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);

  const fetchPage = useCallback(
    async (page: number, mode: "replace" | "append" | "poll") => {
      if (!enabled) return;
      if (mode === "append" && (loadingRef.current || loadingMoreRef.current)) return;

      const reqId = ++reqIdRef.current;
      if (mode === "replace") {
        loadingRef.current = true;
        setLoading(true);
      } else if (mode === "append") {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const size =
          mode === "poll"
            ? Math.min(1000, Math.max(pageSize, listRef.current.length || pageSize))
            : pageSize;
        const res = await getVideoInferPageApi({
          pageNo: mode === "poll" ? 1 : page,
          pageSize: size,
          ...(name ? { name } : {}),
          ...(status ? { status } : {}),
        });
        if (reqId !== reqIdRef.current) return;

        const next = res.data?.list ?? [];
        const totalNum = Number(res.data?.total);
        const safeTotal = Number.isFinite(totalNum) ? totalNum : 0;
        totalRef.current = safeTotal;
        setTotal(safeTotal);
        setHasPending(Boolean(res.data?.hasPending));

        let loaded = next.length;
        if (mode === "append") {
          const merged = [
            ...listRef.current,
            ...next.filter((item) => !listRef.current.some((row) => row.id === item.id)),
          ];
          listRef.current = merged;
          setList(merged);
          pageNoRef.current = page;
          loaded = merged.length;
        } else {
          listRef.current = next;
          setList(next);
          if (mode === "replace") pageNoRef.current = 1;
        }

        if (safeTotal > 0) setHasMore(loaded < safeTotal);
        else setHasMore(next.length >= pageSize);
      } catch {
        if (reqId !== reqIdRef.current) return;
        if (mode === "replace") {
          listRef.current = [];
          totalRef.current = 0;
          setList([]);
          setTotal(0);
          setHasPending(false);
          setHasMore(false);
        }
      } finally {
        if (reqId !== reqIdRef.current) return;
        if (mode === "replace") {
          loadingRef.current = false;
          setLoading(false);
        } else if (mode === "append") {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        }
      }
    },
    [enabled, pageSize, name, status]
  );

  useEffect(() => {
    pageNoRef.current = 1;
    listRef.current = [];
    totalRef.current = 0;
    setList([]);
    setTotal(0);
    setHasMore(false);
    void fetchPage(1, "replace");
  }, [fetchPage]);

  hasMoreRef.current = hasMore;

  const loadMore = useCallback(() => {
    if (!enabled || !hasMoreRef.current || loadingRef.current || loadingMoreRef.current) return;
    void fetchPage(pageNoRef.current + 1, "append");
  }, [enabled, fetchPage]);

  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const tryLoadMoreIfNearBottom = useCallback(() => {
    if (!hasMoreRef.current || loadingRef.current || loadingMoreRef.current) return;
    const scroller = scrollerRef.current;
    const sentinel = sentinelNodeRef.current;
    if (scroller) {
      const remain = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (remain <= LOAD_MORE_MARGIN) {
        loadMoreRef.current();
        return;
      }
    }
    if (sentinel) {
      const rect = sentinel.getBoundingClientRect();
      const viewBottom = scroller?.getBoundingClientRect().bottom ?? window.innerHeight;
      if (rect.top <= viewBottom + LOAD_MORE_MARGIN) loadMoreRef.current();
    }
  }, []);

  const unbindScroll = () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.removeEventListener("scroll", tryLoadMoreIfNearBottom);
      scrollerRef.current = null;
    }
  };

  const bindSentinel = useCallback(
    (node: HTMLDivElement | null) => {
      unbindScroll();
      sentinelNodeRef.current = node;
      if (!node || !enabled) return;

      const root = findScrollParent(node);
      scrollerRef.current = root;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current();
        },
        { root, rootMargin: `${LOAD_MORE_MARGIN}px` }
      );
      observerRef.current.observe(node);
      if (root) {
        root.addEventListener("scroll", tryLoadMoreIfNearBottom, { passive: true });
      }
      tryLoadMoreIfNearBottom();
    },
    [enabled, tryLoadMoreIfNearBottom]
  );

  useEffect(() => {
    if (!enabled || !hasMore || loading || loadingMore) return;
    if (!sentinelNodeRef.current) return;
    tryLoadMoreIfNearBottom();
  }, [enabled, hasMore, loading, loadingMore, list.length, tryLoadMoreIfNearBottom]);

  useEffect(() => {
    if (!enabled || !hasPending) return;
    const timer = window.setInterval(() => {
      void fetchPage(1, "poll");
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, hasPending, fetchPage]);

  useEffect(() => () => unbindScroll(), [tryLoadMoreIfNearBottom]);

  const reload = useCallback(() => {
    listRef.current = [];
    totalRef.current = 0;
    pageNoRef.current = 1;
    setList([]);
    setTotal(0);
    setHasMore(false);
    void fetchPage(1, "replace");
  }, [fetchPage]);

  /** 静默刷新：不清空列表，保持列表高度，避免删除/重命名后页面跳回顶部 */
  const refresh = useCallback(() => {
    pageNoRef.current = 1;
    void fetchPage(1, "poll");
  }, [fetchPage]);

  const removeItem = useCallback((id: number) => {
    const prev = listRef.current;
    const next = prev.filter((row) => row.id !== id);
    if (next.length === prev.length) return;
    const nextTotal = Math.max(0, totalRef.current - 1);
    totalRef.current = nextTotal;
    listRef.current = next;
    setList(next);
    setTotal(nextTotal);
    setHasMore(next.length < nextTotal);
  }, []);

  const patchItem = useCallback((id: number, patch: Partial<VideoInfer.Item>) => {
    const next = listRef.current.map((row) => (row.id === id ? { ...row, ...patch } : row));
    listRef.current = next;
    setList(next);
  }, []);

  return {
    list,
    total,
    loading,
    loadingMore,
    hasMore,
    hasPending,
    loadMore,
    reload,
    refresh,
    removeItem,
    patchItem,
    sentinelRef: bindSentinel,
  };
}
